import React from 'react';
import { Badge } from '../ui/badge';
import {
    Clock, Wrench, CheckCircle, AlertCircle, XCircle,
    Calendar, MapPin, DollarSign, ChevronRight, User, Droplet
} from 'lucide-react';
import { cn } from '../ui/utils';
import { ServiceTicket, TicketStatus } from '../../utils/services/serviceTicketService';

interface TicketListProps {
    tickets: ServiceTicket[];
    onTicketClick: (ticket: ServiceTicket) => void;
    isDarkMode: boolean;
}

export function TicketList({ tickets, onTicketClick, isDarkMode }: TicketListProps) {

    // Status helpers - Legacy Gradients
    const getStatusColor = (status: TicketStatus) => {
        switch (status) {
            case 'open': return 'from-blue-500 to-cyan-600';
            case 'in_progress': return 'from-orange-500 to-amber-600';
            case 'resolved': return 'from-emerald-500 to-green-600';
            case 'cancelled': return 'from-slate-500 to-gray-600';
            default: return 'from-slate-500 to-gray-600';
        }
    };

    const getStatusIcon = (status: TicketStatus) => {
        switch (status) {
            case 'open': return Clock;
            case 'in_progress': return Wrench;
            case 'resolved': return CheckCircle;
            case 'cancelled': return XCircle;
            default: return AlertCircle;
        }
    };

    const getPriorityStyle = (priority: string) => {
        switch (priority) {
            case 'critical':
                return 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20';
            case 'high':
                return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20';
            case 'medium':
                return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20';
            default:
                return 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20';
        }
    };

    if (tickets.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className={cn("p-4 rounded-full mb-4", isDarkMode ? "bg-slate-800" : "bg-gray-100")}>
                    <Clock className={cn("w-8 h-8", isDarkMode ? "text-slate-500" : "text-gray-400")} />
                </div>
                <h3 className={cn("text-lg font-medium", isDarkMode ? "text-white" : "text-gray-900")}>
                    No tickets found
                </h3>
                <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                    Try adjusting your filters or search terms
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {tickets.map(ticket => {
                const StatusIcon = getStatusIcon(ticket.status);

                return (
                    <div
                        key={ticket.id}
                        onClick={() => onTicketClick(ticket)}
                        className={cn(
                            "p-0.5 rounded-2xl shadow-sm cursor-pointer transition-transform hover:scale-[1.01]",
                            `bg-gradient-to-br ${getStatusColor(ticket.status)}`
                        )}
                    >
                        <div className={cn(
                            "rounded-[0.9rem] p-4 h-full",
                            isDarkMode ? "bg-slate-900" : "bg-white"
                        )}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-start gap-3 flex-1">
                                    {/* Icon Box */}
                                    <div className={cn(
                                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                                        `bg-gradient-to-br ${getStatusColor(ticket.status)}`
                                    )}>
                                        <StatusIcon className="w-5 h-5 text-white" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <h3 className={cn(
                                                "text-base font-semibold truncate pr-2",
                                                isDarkMode ? "text-white" : "text-gray-900"
                                            )}>
                                                {ticket.title}
                                            </h3>
                                            <span className={cn(
                                                "text-xs font-mono",
                                                isDarkMode ? "text-gray-500" : "text-gray-400"
                                            )}>
                                                #{ticket.ticketNumber}
                                            </span>
                                        </div>

                                        <div className={cn(
                                            "text-xs mb-2",
                                            isDarkMode ? "text-gray-400" : "text-gray-500"
                                        )}>
                                            {ticket.assignedToName || 'Unassigned'} • {new Date(ticket.createdAt).toLocaleDateString()}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {/* Priority Badge */}
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-md text-xs font-medium",
                                                getPriorityStyle(ticket.priority)
                                            )}>
                                                {ticket.priority.toUpperCase()}
                                            </span>

                                            {/* SLA Breached Badge */}
                                            {ticket.slaBreached && (
                                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-red-600 text-white border border-red-700">
                                                    SLA BREACHED
                                                </span>
                                            )}

                                            {/* Well Name Badge */}
                                            {ticket.wellName && (
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded-md text-xs flex items-center gap-1",
                                                    isDarkMode ? "bg-slate-800 text-gray-300" : "bg-gray-100 text-gray-700"
                                                )}>
                                                    <Droplet className="w-3 h-3" />
                                                    {ticket.wellName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className={cn(
                                "mt-3 pt-3 border-t flex justify-between items-center",
                                isDarkMode ? "border-slate-800" : "border-gray-100"
                            )}>
                                <div className={cn("text-xs", isDarkMode ? "text-gray-500" : "text-gray-400")}>
                                    {ticket.status.replace('_', ' ').toUpperCase()}
                                </div>
                                <div className={cn(
                                    "text-xs font-medium flex items-center gap-1",
                                    isDarkMode ? "text-rose-400" : "text-rose-600"
                                )}>
                                    View Details <ChevronRight className="w-3 h-3" />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}